package com.evoapps.evoshift.ui.user

import androidx.compose.runtime.key
import com.evoapps.evoshift.ui.theme.motionContentSize
import evoshift.composeapp.generated.resources.at_work_break_since
import evoshift.composeapp.generated.resources.at_work_breaks_total
import evoshift.composeapp.generated.resources.at_work_status_on_break
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.usecase.AtWorkEntry
import com.evoapps.evoshift.domain.usecase.AtWorkStatus
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.at_work_clock_in
import evoshift.composeapp.generated.resources.at_work_clock_out
import evoshift.composeapp.generated.resources.at_work_nobody_today
import evoshift.composeapp.generated.resources.at_work_show_all
import evoshift.composeapp.generated.resources.at_work_show_less
import evoshift.composeapp.generated.resources.at_work_status_done
import evoshift.composeapp.generated.resources.at_work_status_late
import evoshift.composeapp.generated.resources.at_work_status_not_started
import evoshift.composeapp.generated.resources.at_work_status_working
import evoshift.composeapp.generated.resources.at_work_title
import evoshift.composeapp.generated.resources.at_work_instead_of
import evoshift.composeapp.generated.resources.at_work_to_review
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/** How many people the card lists before "Show all". */
const val TODAY_AT_WORK_ROWS = 6

/** A row's test tag (robots find a person's row by the shift). */
fun atWorkRowTag(shiftId: String) = "at_work_$shiftId"

/**
 * "Today at work" (SH-72): today's assigned shifts at the workplace, by planned start, each with
 * the planned hours, the clock-in and clock-out times so far and where it stands (not started,
 * late, working, done). Tapping a person opens that day of the roster.
 */
@Composable
fun TodayAtWorkCard(
    entries: List<AtWorkEntry>,
    onOpen: (AtWorkEntry) -> Unit,
    modifier: Modifier = Modifier,
    /** SH-121: more about a person under their row (their checklists today). */
    rowExtra: @Composable (AtWorkEntry) -> Unit = {}
) {
    var expanded by remember { mutableStateOf(false) }
    RegularCard(modifier = modifier.fillMaxWidth()) {
        // SH-158: "Show all" unfolds the rest and "Show less" folds it back, smoothly.
        Column(modifier = Modifier.motionContentSize().padding(16.dp)) {
            Text(
                text = stringResource(Res.string.at_work_title),
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                color = DeepNavy.copy(alpha = 0.7f)
            )
            Spacer(Modifier.height(8.dp))
            if (entries.isEmpty()) {
                Text(
                    text = stringResource(Res.string.at_work_nobody_today),
                    style = MaterialTheme.typography.bodyMedium,
                    color = DeepNavy.copy(alpha = 0.7f)
                )
            } else {
                val shown = if (expanded) entries else entries.take(TODAY_AT_WORK_ROWS)
                shown.forEachIndexed { index, entry ->
                    key(entry.shiftId) {
                        if (index > 0) HorizontalDivider(color = DeepNavy.copy(alpha = 0.08f))
                        AtWorkRow(entry, onClick = { onOpen(entry) }, extra = { rowExtra(entry) })
                    }
                }
                if (entries.size > TODAY_AT_WORK_ROWS) {
                    TextButton(onClick = { expanded = !expanded }) {
                        Text(
                            if (expanded) stringResource(Res.string.at_work_show_less)
                            else stringResource(Res.string.at_work_show_all, entries.size),
                            style = MaterialTheme.typography.labelLarge.copy(fontWeight = FontWeight.Bold),
                            color = DeepNavy
                        )
                    }
                }
            }
        }
    }
}

@OptIn(androidx.compose.foundation.layout.ExperimentalLayoutApi::class)
@Composable
private fun AtWorkRow(entry: AtWorkEntry, onClick: () -> Unit, extra: @Composable () -> Unit = {}) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .handCursor()
            .clickable(onClick = onClick)
            .testTag(atWorkRowTag(entry.shiftId))
            .padding(vertical = 8.dp, horizontal = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.weight(1f)) {
            Text(
                text = com.evoapps.evoshift.ui.util.personName(entry.name),
                style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold),
                color = DeepNavy,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            // SH-262: a narrow row moves a time to the next line whole, never breaks it in the middle.
            FlowRow(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(
                    text = "${hourOf(entry.plannedStart)}–${hourOf(entry.plannedEnd)}",
                    style = MaterialTheme.typography.bodyMedium,
                    color = DeepNavy.copy(alpha = 0.8f),
                    maxLines = 1, softWrap = false
                )
                entry.actualStart?.let {
                    Text(
                        stringResource(Res.string.at_work_clock_in, hourOf(it)),
                        style = MaterialTheme.typography.bodyMedium,
                        color = DeepNavy.copy(alpha = 0.8f),
                        maxLines = 1, softWrap = false
                    )
                }
                entry.actualEnd?.let {
                    Text(
                        stringResource(Res.string.at_work_clock_out, hourOf(it)),
                        style = MaterialTheme.typography.bodyMedium,
                        color = DeepNavy.copy(alpha = 0.8f),
                        maxLines = 1, softWrap = false
                    )
                }
            }
            // SH-116: the break going on, else the breaks so far.
            val openBreak = entry.openBreak
            when {
                openBreak != null -> Text(
                    stringResource(Res.string.at_work_break_since, hourOf(openBreak.start)),
                    style = MaterialTheme.typography.labelMedium,
                    color = atWorkStatusColor(AtWorkStatus.ON_BREAK),
                    fontWeight = FontWeight.Bold
                )
                entry.endedBreakMinutes > 0 -> Text(
                    stringResource(Res.string.at_work_breaks_total, entry.endedBreakMinutes.toInt()),
                    style = MaterialTheme.typography.labelMedium,
                    color = DeepNavy.copy(alpha = 0.7f)
                )
            }
            // SH-134: someone came instead of who was planned; clock times waiting for a manager.
            entry.insteadOf?.let {
                Text(stringResource(Res.string.at_work_instead_of, it), style = MaterialTheme.typography.labelMedium, color = DeepNavy.copy(alpha = 0.7f))
            }
            if (entry.toReview) {
                Text(stringResource(Res.string.at_work_to_review), style = MaterialTheme.typography.labelMedium, color = LocationFlagColor, fontWeight = FontWeight.Bold)
            }
            // SH-106: clocked outside the area or without a location.
            locationFlagText(entry.locationFlag)?.let {
                Text(it, style = MaterialTheme.typography.labelMedium, color = LocationFlagColor, fontWeight = FontWeight.Bold)
            }
            extra()
        }
        Spacer(Modifier.width(8.dp))
        StatusChip(entry.status)
    }
}

@Composable
private fun StatusChip(status: AtWorkStatus) {
    val color = atWorkStatusColor(status)
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(50))
            .background(color.copy(alpha = 0.14f))
            .padding(horizontal = 8.dp, vertical = 3.dp)
    ) {
        // SH-262: a compact badge on one line ("Zakończona"), so the name and times keep the width.
        Text(
            text = stringResource(atWorkStatusLabel(status)),
            style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold),
            color = color,
            maxLines = 1, softWrap = false
        )
    }
}

fun atWorkStatusLabel(status: AtWorkStatus): StringResource = when (status) {
    AtWorkStatus.NOT_STARTED -> Res.string.at_work_status_not_started
    AtWorkStatus.LATE -> Res.string.at_work_status_late
    AtWorkStatus.WORKING -> Res.string.at_work_status_working
    AtWorkStatus.ON_BREAK -> Res.string.at_work_status_on_break
    AtWorkStatus.DONE -> Res.string.at_work_status_done
}

private fun atWorkStatusColor(status: AtWorkStatus): Color = when (status) {
    AtWorkStatus.NOT_STARTED -> Color(0xFF5F6B7A)
    AtWorkStatus.LATE -> Color(0xFFD32F2F)
    AtWorkStatus.WORKING -> Color(0xFF2E7D32)
    AtWorkStatus.ON_BREAK -> Color(0xFF8A5A00)
    AtWorkStatus.DONE -> Color(0xFF1976D2)
}

/** "08:00" of a local ISO date-time ("2026-02-02T08:00:00"); other text as it is. */
internal fun hourOf(isoDateTime: String): String =
    if (isoDateTime.length >= 16 && isoDateTime[10] == 'T') isoDateTime.substring(11, 16) else isoDateTime
